import { Injectable } from '@angular/core';

export interface KnowledgeArticle {
  order: number;
  mainCategory: string;
  subCategory: string;
  question: string;
  response: string;
}

@Injectable({
  providedIn: 'root'
})
export class TraceFormatterService {

  /**
   * Get tool name from trace data
   * Handles both real-time messages (data) and stored messages (rawTrace/body)
   */
  getToolName(trace: any): string {
    // Real-time messages have data property
    if (trace?.data?.tool_name) {
      return trace.data.tool_name;
    }
    
    // Stored messages have rawTrace property
    if (trace?.rawTrace?.body?.tool_name) {
      return trace.rawTrace.body.tool_name;
    }
    
    // Direct body access
    if (trace?.body?.tool_name) {
      return trace.body.tool_name;
    }
    
    return 'Unknown';
  }

  /**
   * Get tool parameters from trace data
   * Handles both real-time messages (data) and stored messages (rawTrace/body)
   */
  getToolParams(trace: any): Array<{name: string, value: any}> {
    let params: any = null;
    
    // Real-time messages have data property
    if (trace?.data?.params) {
      params = trace.data.params;
    }
    // Stored messages have rawTrace property
    else if (trace?.rawTrace?.body?.params) {
      params = trace.rawTrace.body.params;
    }
    // Direct body access
    else if (trace?.body?.params) {
      params = trace.body.params;
    }
    
    if (!params) return [];
    
    // If params is already an array
    if (Array.isArray(params)) {
      return params;
    }
    
    // If params is an object, convert to array format
    if (typeof params === 'object') {
      return Object.entries(params).map(([name, value]) => ({ name, value }));
    }
    
    return [];
  }

  /**
   * Check if content is a knowledge base response
   */
  isKnowledgeBaseResponse(text: unknown): boolean {
    if (typeof text !== 'string') return false;
    if (!text) return false;
    return text.includes('<knowledge_base_response>') && text.includes('</knowledge_base_response>');
  }

  /**
   * Parse knowledge base response XML into articles
   */
  parseKnowledgeBaseResponse(text: unknown): KnowledgeArticle[] {
    if (typeof text !== 'string' || !text) return [];
    
    try {
      const articles: KnowledgeArticle[] = [];
      const articleRegex = /<article>([\s\S]*?)<\/article>/g;
      let match;
      
      while ((match = articleRegex.exec(text)) !== null) {
        const articleContent = match[1];
        
        const order = this.extractXmlValue(articleContent, 'order');
        const mainCategory = this.extractXmlValue(articleContent, 'main_category');
        const subCategory = this.extractXmlValue(articleContent, 'sub_category');
        const question = this.extractXmlValue(articleContent, 'question');
        const response = this.extractXmlValue(articleContent, 'response');
        
        articles.push({
          order: Number.parseInt(order) || articles.length + 1,
          mainCategory,
          subCategory,
          question,
          response
        });
      }
      
      return articles;
    } catch (error) {
      console.error('Error parsing knowledge base response:', error);
      return [];
    }
  }

  /**
   * Extract value from XML tag
   */
  private extractXmlValue(content: string, tag: string): string {
    const regex = new RegExp(String.raw`<${tag}>([\s\S]*?)<\/?${tag}>`);
    const match = regex.exec(content);
    return match ? match[1].trim() : '';
  }

  /**
   * Check if content is a valid JSON output
   */
  isJsonOutput(text: unknown): boolean {
    if (text === null || text === undefined) return false;
    if (typeof text === 'object') return true;
    if (typeof text !== 'string' || !text) return false;

    const trimmed = text.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return false;
    
    try {
      JSON.parse(trimmed);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Format JSON with syntax highlighting
   */
  formatJsonOutput(text: unknown): string {
    if (text === null || text === undefined) return '';

    try {
      const parsed = typeof text === 'string' ? JSON.parse(text.trim()) : text;
      const pretty = JSON.stringify(parsed, null, 2);
      
      // Apply syntax highlighting
      let formatted = pretty
      return formatted;
    } catch (error) {
      console.error('Error formatting JSON:', error);
      if (typeof text === 'string') return text;
      try {
        return JSON.stringify(text, null, 2);
      } catch {
        return String(text);
      }
    }
  }

  // ============================================
  // AGENT TRACE NORMALIZATION & TRANSFORMATION
  // ============================================

  /**
   * Normalizes agentTrace to display format [{ agent_name, traces: [...] }].
   * Handles: raw API format, already-processed display format, JSON strings, null/undefined.
   */
  normalizeAgentTrace(agentTrace: any, agentName?: string | null): any[] | undefined {
    if (!agentTrace) return undefined;

    let traceArray: any[] | undefined;

    if (typeof agentTrace === 'string') {
      try {
        const parsed = JSON.parse(agentTrace);
        if (Array.isArray(parsed)) traceArray = parsed;
      } catch {
        return undefined;
      }
    } else if (Array.isArray(agentTrace)) {
      traceArray = agentTrace;
    }

    if (!traceArray || traceArray.length === 0) return undefined;

    // Already in display format → pass through
    if (this.isDisplayFormat(traceArray)) return traceArray;

    // Raw API format → transform
    return this.transformRawToDisplay(traceArray, agentName);
  }

  private isDisplayFormat(trace: any[]): boolean {
    const first = trace[0];
    if (typeof first !== 'object' || first === null || Array.isArray(first)) return false;
    return ('agent_name' in first || 'agentName' in first) && Array.isArray(first.traces);
  }

  private transformRawToDisplay(rawTrace: any[], fallbackAgentName: string | null | undefined = 'Unknown Agent'): any[] {
    const agentMap = new Map<string, any[]>();
    let currentAgent = fallbackAgentName ?? 'Unknown Agent';

    for (const item of rawTrace) {
      if (typeof item === 'string') {
        currentAgent = this.processStringItem(item.trim(), agentMap, currentAgent);
      } else if (item && typeof item === 'object') {
        currentAgent = this.processObjectItem(item, agentMap, currentAgent);
      }
    }

    return Array.from(agentMap.entries()).map(([agent_name, traces]) => ({ agent_name, traces }));
  }

  private processStringItem(text: string, agentMap: Map<string, any[]>, currentAgent: string): string {
    if (text.startsWith('<answer>')) {
      this.addTrace(agentMap, currentAgent, { type: 'final_answer', timestamp: '', body: { text: text.replace(/^<answer>\s*/, '').trim() } });
      return currentAgent;
    }
    try {
      return this.processObjectItem(JSON.parse(text), agentMap, currentAgent);
    } catch {
      if (text.length > 0) {
        this.addTrace(agentMap, currentAgent, { type: 'final_answer', timestamp: '', body: { text } });
      }
      return currentAgent;
    }
  }

  private processObjectItem(parsed: any, agentMap: Map<string, any[]>, currentAgent: string): string {
    if (parsed.session_id && Array.isArray(parsed.content)) {
      return this.processContentArray(parsed, agentMap, currentAgent);
    }
    if (parsed.reasoningContent) {
      this.addTrace(agentMap, currentAgent, {
        type: 'thinking', timestamp: '',
        body: { text: parsed.reasoningContent?.reasoningText?.text || '' }
      });
    }
    return currentAgent;
  }

  private processContentArray(parsed: any, agentMap: Map<string, any[]>, currentAgent: string): string {
    const agent = parsed.path_selected || currentAgent;
    const ts = parsed.timestamp || '';
    for (const ci of parsed.content) {
      this.processContentItem(ci, parsed.message_type, ts, agent, agentMap);
    }
    return agent;
  }

  private processContentItem(ci: any, messageType: string, ts: string, agent: string, agentMap: Map<string, any[]>): void {
    if (messageType === 'orchestration' && ci.text) {
      for (const t of this.parseOrchestrationTraces(ci.text, ts)) {
        this.addTrace(agentMap, agent, t);
      }
      return;
    }
    const { trace, traceAgent } = this.buildContentTrace(ci, messageType, ts, agent);
    if (trace) this.addTrace(agentMap, traceAgent, trace);
  }

  private buildContentTrace(ci: any, messageType: string, ts: string, agent: string): { trace: Record<string, any> | null; traceAgent: string } {
    if (messageType === 'summary' && ci.text) {
      return { trace: { type: 'info', timestamp: ts, body: { text: ci.text } }, traceAgent: 'summarizer' };
    }
    if (messageType === 'msg' && ci.text) {
      const text = ci.text.trim().replace(/^<answer>\s*/, '').trim();
      return { trace: { type: 'final_answer', timestamp: ts, body: { text } }, traceAgent: agent };
    }
    return { trace: this.convertContentItem(ci, ts), traceAgent: agent };
  }

  private parseOrchestrationTraces(text: string, timestamp: string): Record<string, any>[] {
    const thinkingMatch = text.match(/^([\s\S]*?)<\/thinking>/);
    const thinkingText = thinkingMatch ? thinkingMatch[1].trim() : text;
    const pathSelected = this.extractXmlValue(text, 'path_selected');
    const traces: Record<string, any>[] = [
      { type: 'thinking', timestamp, body: { text: thinkingText } }
    ];
    if (pathSelected) {
      traces.push({ type: 'final_answer', timestamp, body: { text: `Path seleccionado: ${pathSelected}` } });
    }
    return traces;
  }

  private convertContentItem(ci: any, timestamp: string): Record<string, any> | null {
    if (ci.reasoningContent) return this.toThinkingTrace(ci, timestamp);
    if (ci.toolUse)          return this.toToolUseTrace(ci, timestamp);
    if (ci.toolResult)       return this.toToolOutputTrace(ci, timestamp);
    if (ci.text)             return this.toTextTrace(ci.text, timestamp);
    return null;
  }

  private toThinkingTrace(ci: any, timestamp: string): Record<string, any> {
    return { type: 'thinking', timestamp, body: { text: ci.reasoningContent?.reasoningText?.text || '' } };
  }

  private toToolUseTrace(ci: any, timestamp: string): Record<string, any> {
    const params = Object.entries(ci.toolUse.input || {}).map(([name, value]) => ({
      name, value: typeof value === 'string' ? value : JSON.stringify(value)
    }));
    return { type: 'tool_use', timestamp, body: { tool_name: ci.toolUse.name || 'Unknown', params } };
  }

  private toToolOutputTrace(ci: any, timestamp: string): Record<string, any> {
    const raw = ci.toolResult.content?.[0]?.text;
    let text = raw ?? '';
    if (raw) { try { text = JSON.parse(raw).body || raw; } catch { text = raw; } }
    return { type: 'tool_output', timestamp, body: { text } };
  }

  private toTextTrace(text: string, timestamp: string): Record<string, any> {
    return { type: 'info', timestamp, body: { text } };
  }

  private addTrace(agentMap: Map<string, any[]>, agentName: string, trace: any): void {
    if (!agentMap.has(agentName)) agentMap.set(agentName, []);
    agentMap.get(agentName)!.push(trace);
  }

  /**
   * Group consecutive traces by agent name
   */
  groupTracesByAgent(traces: any[]): { agentName: string; traces: any[]; timestamp: Date | null }[] {
    if (!traces || traces.length === 0) return [];
    
    const groups: { agentName: string; traces: any[]; timestamp: Date | null }[] = [];
    let currentGroup: { agentName: string; traces: any[]; timestamp: Date | null } | null = null;
    
    traces.forEach(trace => {
      const agentName = trace.agentName || 'Agent';
      
      if (!currentGroup || currentGroup.agentName !== agentName) {
        // Nuevo agente - crear nuevo grupo
        currentGroup = {
          agentName,
          traces: [trace],
          timestamp: trace.timestamp
        };
        groups.push(currentGroup);
      } else {
        // Mismo agente - agregar a grupo actual
        currentGroup.traces.push(trace);
      }
    });
    
    return groups;
  }
}
